import { useTheme } from 'next-themes';
import { useMounted } from '@/hooks/mounted.hook';
import { cn } from '@/utils/classnames';
import { motion } from 'framer-motion';

export default function ThemeToggle({ className }: { className?: string }) {
  const mounted = useMounted();
  const { theme, setTheme } = useTheme();

  if (!mounted) {
    return <div className="w-[8.4rem] h-[4.2rem] rounded-[5rem] bg-interactive animate-pulse" />;
  }

  const isDark = theme === 'dark';

  return (
    <button
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={cn(
        'w-[8.4rem] h-[4.2rem] rounded-[5rem] bg-interactive p-[0.4rem] px-[0.5rem] flex items-center relative transition-all duration-300 focus:outline-none group',
        className,
      )}
      aria-label="Toggle Theme"
    >
      {/* Dynamic Background Indicator */}
      <motion.div
        layout
        initial={false}
        animate={{
          x: isDark ? 0 : '4rem',
        }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30,
        }}
        className="w-[3.4rem] h-[3.4rem] rounded-full bg-white shadow-sm z-10"
      />

      {/* Icons Container */}
      <div className="absolute inset-0 px-[0.5rem] py-[0.4rem] flex justify-between items-center pointer-events-none">
        <div className="w-[3.4rem] h-[3.4rem] flex items-center justify-center z-20">
          <MoonIcon
            className={cn(
              'w-[2rem] h-[2rem] transition-colors duration-300',
              isDark ? 'icon-main' : 'icon-main opacity-50',
            )}
          />
        </div>
        <div className="w-[3.4rem] h-[3.4rem] flex items-center justify-center z-20">
          <SunIcon
            className={cn(
              'w-[2rem] h-[2rem] transition-colors duration-300',
              !isDark ? 'icon-main' : 'icon-main opacity-50',
            )}
          />
        </div>
      </div>
    </button>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}
